import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const AuthContext = createContext(null);

const INITIAL_STATE = {
    loading: true,
    loggedIn: false,
    hasProfile: false,
    user: null,
    error: null
};

export function AuthProvider({ children }) {
    const [state, setState] = useState(INITIAL_STATE);

    const refresh = useCallback(async () => {
        try {
            setState(prev => ({
                ...prev,
                loading: true,
                error: null
            }));

            const res = await fetch('/api/auth/auth_status.php', {
                method: 'GET',
                credentials: 'include'
            });

            const json = await res.json();

            if (!json?.success) {
                setState({
                    loading: false,
                    loggedIn: false,
                    hasProfile: false,
                    user: null,
                    error: null
                });
                return;
            }

            const loggedIn = Boolean(json?.data?.logged_in);
            const hasProfile = Boolean(json?.data?.has_profile);
            const user = json?.data?.user ?? null;

            setState({
                loading: false,
                loggedIn,
                hasProfile,
                user,
                error: null
            });
        } catch (e) {
            setState({
                loading: false,
                loggedIn: false,
                hasProfile: false,
                user: null,
                error: e?.message || 'Auth status feilet'
            });
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await fetch('/api/auth/logout.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'logout' })
            });
        } finally {
            setState({
                loading: false,
                loggedIn: false,
                hasProfile: false,
                user: null,
                error: null
            });

            document.dispatchEvent(
                new CustomEvent('sar61:auth-changed', {
                    detail: { loggedIn: false }
                })
            );
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const value = useMemo(
        () => ({
            ...state,
            refresh,
            logout
        }),
        [state, refresh, logout]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error('useAuthContext must be used within an AuthProvider');
    }

    return context;
}
